import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Box, Paper, Typography, Alert } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useCoverBatchUpload } from './hooks/useCoverBatchUpload';
import CoverFileList from './components/CoverFileList';
import { BATCH_WARNING_THRESHOLD_BYTES } from './utils/coverFileUtils';

function DropZone({ onFilesSelect }) {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const dropped = Array.from(e.dataTransfer.files).filter((f) =>
      f.name.toLowerCase().endsWith('.pdf')
    );
    if (dropped.length) onFilesSelect(dropped);
  }, [onFilesSelect]);

  const handleChange = useCallback((e) => {
    const selected = Array.from(e.target.files);
    if (selected.length) onFilesSelect(selected);
    e.target.value = '';
  }, [onFilesSelect]);

  return (
    <Paper
      sx={{
        p: 6,
        mb: 3,
        border: dragActive ? '3px dashed #1976d2' : '2px dashed #ccc',
        backgroundColor: dragActive ? 'rgba(25, 118, 210, 0.05)' : 'transparent',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        textAlign: 'center',
      }}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        multiple
        onChange={handleChange}
        style={{ display: 'none' }}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <CloudUploadIcon sx={{ fontSize: 48, color: dragActive ? 'primary.main' : 'text.secondary' }} />
        <Typography variant="h5" color={dragActive ? 'primary.main' : 'text.primary'}>
          {dragActive ? 'Drop PDFs here' : 'Drag & Drop PDFs'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          or click to select — multiple files supported, filenames must contain an EAN-13
        </Typography>
      </Box>
    </Paper>
  );
}

function downloadBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function AddCoverService() {
  const { files, addFiles, retry } = useCoverBatchUpload();

  const totalBytes = useMemo(
    () => files.reduce((sum, entry) => sum + entry.file.size, 0),
    [files]
  );
  const showBatchWarning = totalBytes > BATCH_WARNING_THRESHOLD_BYTES;

  const handleDownload = (entry, stageName) => {
    const blob = entry.stages[stageName].resultBlob;
    if (blob) downloadBlob(entry.filename, blob);
  };

  const handleRetry = (entry, stageName) => {
    if (stageName === 'cover') retry(entry);
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4, px: 2 }}>
      <Typography variant="h4" gutterBottom>
        Add Cover
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Upload PDFs whose filenames contain an EAN-13. Each file's matching cover is fetched from
        S3, bound as a PDF/UA-1 first page, and made available to download once ready.
      </Typography>

      <DropZone onFilesSelect={addFiles} />

      {showBatchWarning && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          This batch totals over 1GB — uploads may take a while depending on your connection.
        </Alert>
      )}

      <CoverFileList files={files} onDownload={handleDownload} onRetry={handleRetry} />
    </Box>
  );
}

export default AddCoverService;
